import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { EtimsDocumentView, EtimsFacilityMode, EtimsSettingsView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  RadioGroup,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  etimsDocumentsQuery,
  etimsKeys,
  etimsSettingsQuery,
  saveEtimsMode,
  signDocumentAgain,
} from '../api/etims.ts';
import { can, sessionQuery } from '../api/session.ts';
import { DOCUMENT_TYPE_LABELS, FISCAL_STATE_LABELS, MODE_LABELS } from '../billing/etims-labels.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

/**
 * Settings > eTIMS (docs/plans/phase3-etims-vscu.md §6; ADR 0041): how this facility signs
 * its tax documents, what the VSCU may still sign offline, and the documents waiting on
 * eTIMS with the reason and next step for each.
 */
export function EtimsSettingsPage() {
  const settings = useQuery(etimsSettingsQuery);
  const { data: session } = useSuspenseQuery(sessionQuery);
  const mayManage = session !== null && can(session, 'etims.settings.manage');
  const maySignAgain = session !== null && can(session, 'etims.documents.sign_again');
  return (
    <SettingsPage title="eTIMS">
      {settings.isPending ? <LoadingBlock label="Loading eTIMS settings" rows={4} /> : null}
      {settings.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void settings.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(settings.error, "eTIMS settings couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {settings.isSuccess ? (
        <>
          <ModeSection key={settings.data.version} settings={settings.data} mayManage={mayManage} />
          {settings.data.mode === 'vscu' || settings.data.offline.count > 0 ? (
            <OfflineSection settings={settings.data} />
          ) : null}
          <DocumentsSection mode={settings.data.mode} maySignAgain={maySignAgain} />
        </>
      ) : null}
    </SettingsPage>
  );
}

function ModeSection({ settings, mayManage }: { settings: EtimsSettingsView; mayManage: boolean }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<EtimsFacilityMode>(settings.mode);
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => saveEtimsMode(mode, reason.trim(), settings.version),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: etimsKeys.all });
      toasts.show({ title: 'eTIMS mode saved' });
    },
  });
  const unavailable = (m: EtimsFacilityMode) => m !== 'off' && !settings.availableModes.includes(m);
  const options = (['off', 'oscu', 'vscu'] as const).map((m) => ({
    id: m,
    label: MODE_LABELS[m].label,
    description: unavailable(m)
      ? `This server can't sign through ${MODE_LABELS[m].label} yet.`
      : MODE_LABELS[m].description,
    isDisabled: unavailable(m),
  }));
  if (!mayManage)
    return (
      <Section title="How this facility signs">
        <p>
          <strong>{MODE_LABELS[settings.mode].label}.</strong>{' '}
          {MODE_LABELS[settings.mode].description}
        </p>
      </Section>
    );
  const unchanged = mode === settings.mode;
  return (
    <Section title="How this facility signs">
      <RadioGroup
        label="eTIMS mode"
        options={options}
        value={mode}
        onChange={(value) => {
          setMode(value as EtimsFacilityMode);
        }}
      />
      <TextField
        label="Reason for the change"
        description="Recorded in the audit log with the mode before and after."
        value={reason}
        onChange={setReason}
        isDisabled={unchanged}
      />
      {save.isError ? (
        <Notice tone="error">{errorText(save.error, "The mode wasn't changed. Try again.")}</Notice>
      ) : null}
      <div>
        <Button
          variant="primary"
          isPending={save.isPending}
          pendingLabel="Saving"
          isDisabled={!online || unchanged || reason.trim().length < 3}
          onPress={() => {
            save.mutate();
          }}
        >
          Save mode
        </Button>
      </div>
    </Section>
  );
}

function OfflineSection({ settings }: { settings: EtimsSettingsView }) {
  const { offline } = settings;
  const { allowance } = offline;
  const limitWords = `Offline signing is allowed for ${String(offline.limits.maxHours)} hours${
    offline.limits.maxDocuments === null
      ? ''
      : ` or ${String(offline.limits.maxDocuments)} documents, whichever comes first`
  }.`;
  return (
    <Section title="Signing offline (VSCU)">
      {offline.count === 0 ? (
        <p>Every document the VSCU signed has reached KRA. {limitWords}</p>
      ) : (
        <p>
          {`${String(offline.count)} ${offline.count === 1 ? 'document' : 'documents'} signed offline, waiting to send to KRA.`}
          {offline.oldestSignedAt === null
            ? null
            : ` The oldest was signed ${formatInstant(offline.oldestSignedAt)}.`}{' '}
          {limitWords}
        </p>
      )}
      {allowance.allowed ? (
        allowance.signingStopsAt === null ? null : (
          <p>
            {`Offline signing stops ${formatInstant(allowance.signingStopsAt)} unless these reach KRA first.`}
            {allowance.documentsLeft === null
              ? null
              : ` ${String(allowance.documentsLeft)} more can be signed offline.`}
          </p>
        )
      ) : (
        <Notice tone="info">{`${allowance.message} ${allowance.nextStep}`}</Notice>
      )}
    </Section>
  );
}

function DocumentsSection({
  mode,
  maySignAgain,
}: {
  mode: EtimsFacilityMode;
  maySignAgain: boolean;
}) {
  const documents = useQuery(etimsDocumentsQuery);
  return (
    <Section title="Documents waiting on eTIMS">
      {documents.isPending ? <LoadingBlock label="Loading documents" rows={3} /> : null}
      {documents.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void documents.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(documents.error, "The documents couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {documents.isSuccess ? (
        <TableScroll>
          <Table flat aria-label="Documents waiting on eTIMS">
            <TableHeader>
              <Column isRowHeader>Document</Column>
              <Column>Type</Column>
              <Column>State</Column>
              <Column>Why, and what to do</Column>
              <Column>Action</Column>
            </TableHeader>
            <TableBody
              items={documents.data.items.map((d) => ({ ...d, id: d.signingId }))}
              renderEmptyState={() =>
                mode === 'off'
                  ? 'eTIMS is off, so documents are not signed.'
                  : 'Every document is signed and has reached KRA.'
              }
            >
              {(d) => <DocumentRow document={d} maySignAgain={maySignAgain} />}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
    </Section>
  );
}

function DocumentRow({
  document: d,
  maySignAgain,
}: {
  document: EtimsDocumentView & { id: string };
  maySignAgain: boolean;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const again = useMutation({
    mutationFn: () => signDocumentAgain(d.signingId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: etimsKeys.all });
      toasts.show({ title: 'Sent for signing again', description: d.number });
    },
    onError: (error) => {
      toasts.show({ title: errorText(error, "It wasn't sent again. Try again.") });
    },
  });
  const state = FISCAL_STATE_LABELS[d.state];
  return (
    <Row id={d.id}>
      <Cell>
        <span className="wodi-id">{d.number}</span>
      </Cell>
      <Cell>{DOCUMENT_TYPE_LABELS[d.documentType]}</Cell>
      <Cell>
        <Tag state={state.tag}>{state.label}</Tag>
      </Cell>
      <Cell>{d.problem === null ? '' : `${d.problem.message} ${d.problem.nextStep}`}</Cell>
      <Cell>
        {d.canSignAgain && maySignAgain ? (
          <Button
            dense
            isPending={again.isPending}
            pendingLabel="Sending"
            isDisabled={!online}
            onPress={() => {
              again.mutate();
            }}
          >
            Sign again
          </Button>
        ) : null}
      </Cell>
    </Row>
  );
}
